import { roundToDecimals } from '@dolfin/utils/numbers'
import { DateFormatter } from '@internationalized/date'
import { isMatch, parseISO } from 'date-fns'
import { type RuleGroupTypeAny, RuleProcessor, RuleType } from 'react-querybuilder'
import { Enums, Tables } from './db_types'
export { doesVersionApplyToUser, evaluateBranchByUserLogic } from './doesVersionApplyToUser'
export {
  isClawbackPayout,
  isDiscrepancyAdjustment,
  isManualPayout,
  isRegularManualPayout
} from './domains/payouts/payoutPredicates'

export const firstToUpperCase = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const breakOnUnderscore = (str: string) => {
  return str.split('_').join(' ')
}

export function removeAccents(value: unknown): string {
  const str = typeof value === 'string' ? value : String(value ?? '')

  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

export function removeEmptyValues<T extends Record<string, unknown>>(obj: T): T {
  return Object.entries(obj)
    .filter(([_, v]) => v != null && v !== '' && v !== undefined)
    .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {} as Record<string, unknown>) as T
}

export function removeKeys<T extends object, K extends keyof T>(
  obj: T,
  keysToRemove: K[]
): Omit<T, K> {
  const newObj = { ...obj }
  for (const key of keysToRemove) {
    delete newObj[key]
  }
  return newObj
}

export const removeObjectFields = (obj: Record<string, unknown>, fields?: string[]) => {
  const newObj = { ...obj }
  for (const field of Object.keys(newObj)) {
    const value = newObj[field]
    if (value?.constructor === Object && !fields?.includes(field) && field !== 'meta') {
      delete newObj[field]
    } else if (
      value?.constructor === Object &&
      value &&
      typeof value === 'object' &&
      'id' in value
    ) {
      delete (value as Record<string, unknown>).id
    }
  }
  return newObj
}

export function flattenObject(
  obj: Record<string, unknown>,
  parentKey = '',
  result = {} as Record<string, unknown>
) {
  if (obj.constructor !== Object) {
    return obj
  }

  for (const key in obj) {
    const currentValue = obj[key]

    if (typeof currentValue === 'object' && currentValue !== null && !Array.isArray(currentValue)) {
      // Recursive call for nested objects, appending `parentKey` with current `key`
      flattenObject(currentValue as Record<string, unknown>, `${parentKey}${key}_`, result)
    } else if (Array.isArray(currentValue)) {
      currentValue.forEach((item: unknown, index: number) => {
        if (!item || typeof item !== 'object') {
          flattenObject({ value: item }, `${parentKey}${key}_${index}_`, result)
          return
        }

        const typedItem = item as Record<string, unknown>
        if (
          Object.keys(typedItem).length === 2 &&
          ('label' in typedItem || Object.keys(typedItem).some(k => k.endsWith('_label'))) &&
          ('sum' in typedItem || 'count' in typedItem)
        ) {
          const matchingLabelEntry = Object.entries(item).find(([k]) => k.endsWith('_label'))?.[1]
          const label =
            typedItem.label ??
            (matchingLabelEntry != null && typeof matchingLabelEntry === 'object'
              ? (matchingLabelEntry as { label?: string }).label
              : matchingLabelEntry)
          if (label != null && label !== '') {
            flattenObject(
              'sum' in typedItem ? { sum: typedItem.sum } : { count: typedItem.count },
              `${parentKey}${key}_${label}_`,
              result
            )
          }
        } else {
          flattenObject(item as Record<string, unknown>, `${parentKey}${key}_${index}_`, result)
        }
      })
    } else {
      // Direct assignment for non-object types or null values
      result[`${parentKey}${key}`] = currentValue
    }
  }
  return result
}

export const getLocaleFromLanguage = (lang?: string) => {
  const locales: {
    [index: string]: {
      locale: string
      options?: { [index: string]: unknown }
    }
  } = {
    es: { locale: 'es-ES', options: { useGrouping: 'always' } },
    en: { locale: 'en-US' },
    de: { locale: 'de-DE', options: { useGrouping: 'always' } }
  }

  return locales[lang ?? 'en'] ?? { locale: 'en-US' }
}

export function formatDateAndNumbers(obj: Record<string, unknown>, language: string) {
  if (language !== 'en') {
    const locale = getLocaleFromLanguage(language).locale
    return Object.keys(obj).reduce(
      (acc, key) => {
        const value = obj[key]
        if (key !== 'id' && !key.endsWith('_id') && !isNaN(Number(value))) {
          acc[key] = new Intl.NumberFormat(locale, {
            style: 'decimal',
            maximumFractionDigits: 2,
            useGrouping: false
          }).format(Number(value))
        } else if (typeof value === 'string' && isMatch(value, 'yyyy-MM-dd')) {
          acc[key] = new DateFormatter(locale).format(parseISO(value))
        } else {
          acc[key] = value
        }
        return acc
      },
      {} as Record<string, unknown>
    )
  }

  return obj
}

export function removeAutoDateFields(obj: Record<string, unknown>) {
  return Object.keys(obj).reduce(
    (acc, key) => {
      if (!key.endsWith('created_at') && !key.endsWith('updated_at')) {
        acc[key] = obj[key]
      }
      return acc
    },
    {} as Record<string, unknown>
  )
}

export function abbreviateNumber(num: number): string {
  if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(0).replace(/\.0$/, '') + 'B'
  } else if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(0).replace(/\.0$/, '') + 'M'
  } else if (num >= 1_000) {
    return (num / 1_000).toFixed(0).replace(/\.0$/, '') + 'K'
  } else {
    return num.toFixed(0).toString()
  }
}

export const comparePeriods = (period1: Enums<'period'>, period2: Enums<'period'>) => {
  const periods = ['month', 'quarter', 'half_year', 'year']
  return periods.indexOf(period1) - periods.indexOf(period2)
}

export const getCompensationValue = (tierValue: string | number, attainment: number): number => {
  const formulaWithVariablesWithValues = String(tierValue).replaceAll(
    /Achievement/gi,
    attainment.toString()
  )

  return new Function(`return ${formulaWithVariablesWithValues}`)()
}

export function isValidCurrencyCode(currency: string) {
  try {
    new Intl.NumberFormat('en-US', { style: 'currency', currency })
    return true
  } catch {
    return false
  }
}

export const fieldIsTarget = (field: string) =>
  /^target_[0-9]+(_(month|quarter|half_year|year))?(_full_period)?$/.test(field)
export const getFieldPeriod = (field: string) => field.match(/_(month|quarter|half_year|year)/)![1]
export const fieldHasPeriod = (field: string) => /_(month|quarter|half_year|year)/i.test(field)
export const removePeriodFromField = (field: string) =>
  field.replace(/_(month|quarter|half_year|year)/i, '')
export const fieldHasFullPeriod = (field: string) => field.endsWith('_full_period')
export const removeFullPeriodFromField = (field: string) => field.replace(/_full_period$/, '')
export const ruleProcessor: (targets: Tables<'targets'>[]) => RuleProcessor =
  targets =>
  (
    r: RuleType & {
      period?: Enums<'period'>
      targetPeriod?: Enums<'period'>
      fullPeriod?: boolean
    }
  ): RuleType & {
    period?: Enums<'period'>
    targetPeriod?: Enums<'period'>
    fullPeriod?: boolean
  } => {
    if (!r.field) return r

    const isTarget = fieldIsTarget(r.field)
    if (!isTarget) return r

    const valueHasPeriod = isTarget && fieldHasPeriod(r.field)
    const valueWithoutPeriod = removeFullPeriodFromField(removePeriodFromField(r.field))
    const period = valueHasPeriod ? getFieldPeriod(r.field) : undefined
    const fullPeriod = fieldHasFullPeriod(r.field)

    const targetPeriod = targets?.find(t => t.id === Number(valueWithoutPeriod.split('_')[1]))
      ?.period as Enums<'period'>
    const currentPeriod = r.period ?? (period as Enums<'period'>)
    const currentFullPeriod = r.fullPeriod ?? fullPeriod

    return {
      ...r,
      field: valueWithoutPeriod,
      targetPeriod,
      period:
        !currentPeriod || comparePeriods(targetPeriod, currentPeriod) > 0
          ? targetPeriod
          : currentPeriod,
      fullPeriod: currentFullPeriod
    }
  }
type JsonLogicVar = { var: string }

const isJsonLogicVar = (value: unknown): value is JsonLogicVar =>
  !!value &&
  typeof value === 'object' &&
  'var' in value &&
  typeof (value as JsonLogicVar).var === 'string'

const buildCustomOperatorRule = (
  operator: RuleType['operator'],
  value: unknown
): RuleType | false => {
  if (!Array.isArray(value) || value.length < 2 || !isJsonLogicVar(value[0])) {
    return false
  }

  const rhs = value[1]
  return {
    field: value[0].var,
    operator,
    value: isJsonLogicVar(rhs) ? rhs.var : rhs,
    valueSource: isJsonLogicVar(rhs) ? 'field' : 'value'
  }
}

export const customOperators: Record<
  string,
  (value: unknown) => RuleType | RuleGroupTypeAny | false
> = {
  startswith: val => buildCustomOperatorRule('startswith', val),
  endswith: val => buildCustomOperatorRule('endswith', val),
  samemonth: val => buildCustomOperatorRule('samemonth', val),
  samequarter: val => buildCustomOperatorRule('samequarter', val),
  futurequarter: val => buildCustomOperatorRule('futurequarter', val),
  pastquarter: val => buildCustomOperatorRule('pastquarter', val),
  contains: val => buildCustomOperatorRule('contains', val),
  doesNotContain: val => buildCustomOperatorRule('doesNotContain', val),
  sameyear: val => buildCustomOperatorRule('sameyear', val)
}

export class DateRangeAsStringParser {
  static parse(dateRangeStr: string): { start: string; end: string } | null {
    const dates = dateRangeStr.slice(1, -1).split(',')
    if (dates.length === 2) {
      return { start: dates[0], end: dates[1] }
    }
    return null
  }

  static containsToday(dateRangeStr: string): boolean {
    const range = this.parse(dateRangeStr)
    if (range) {
      const today = new Date()
      const startDate = parseISO(range.start)
      const endDate = parseISO(range.end)
      return startDate <= today && today <= endDate
    }
    return false
  }
}
export const CONTRIBUTOR_KEYS = ['user', 'team_manager', 'everyone'] as const

export const isValidContributor = (value: unknown): boolean => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  return (
    'key' in value &&
    typeof value.key === 'string' &&
    (CONTRIBUTOR_KEYS as readonly string[]).includes(value.key)
  )
}

export const LLMFormDataReviewer = (
  nodeId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formData: Record<string, any>,
  options?: { isTvpBased?: boolean }
): { error: string | null } => {
  if (!formData || Object.keys(formData).length === 0) {
    return { error: 'Form data is empty' }
  }

  switch (nodeId) {
    case 'bonus': {
      const {
        type,
        reference_type,
        fixed_amount,
        target,
        targets,
        contributor,
        metric,
        tiers,
        minimum_achievement,
        maximum_achievement,
        team_target,
        team_ids
      } = formData

      if (type === 'fixed') {
        if (options?.isTvpBased) return { error: null }
        if (fixed_amount == null) return { error: 'Fixed bonuses require a fixed_amount' }
        return { error: null }
      }

      if (type === 'tiered') {
        if (!tiers?.length) return { error: 'Tiered bonuses require tiers' }
        if (!reference_type) return { error: 'Tiered bonuses require reference_type' }
        if (reference_type === 'target' && !target && (!targets || targets.length === 0))
          return {
            error: 'Target or targets required for target-based tiered bonuses'
          }
        if (reference_type === 'metric' && !metric)
          return { error: 'Metric required for metric-based tiered bonuses' }
      }
      if (reference_type === 'target' && team_target && (!team_ids || team_ids.length === 0))
        return {
          error: 'team_ids must be provided when team_target is true'
        }

      if (type === 'proportional') {
        if (!target && (!targets || targets.length === 0))
          return {
            error: 'Target or targets required for proportional bonuses'
          }
        if (minimum_achievement == null || maximum_achievement == null)
          return {
            error: 'Minimum and maximum achievement required for proportional bonuses'
          }
      }

      if ((reference_type === 'target' || reference_type === 'metric') && !contributor)
        return {
          error: 'Contributor required for target or metric-based tiered bonuses'
        }

      if (contributor && !isValidContributor(contributor))
        return { error: 'Invalid contributor value' }

      break
    }

    case 'commission': {
      const {
        type,
        reference_type,
        percentage,
        fixed_amount,
        calculate_over_column,
        calculate_over_metric,
        column,
        percentage_column,
        metric,
        contributor,
        targets,
        tiers,
        metric_period
      } = formData

      if (type === 'piecework') {
        if (fixed_amount == null) return { error: 'Piecework commissions require a fixed_amount' }
      }

      if (type === 'straight') {
        if (percentage == null) return { error: 'Straight commissions require a percentage' }

        if (reference_type === 'column' && !calculate_over_column)
          return {
            error: 'Must provide calculate_over_column for column-based straight commission'
          }

        if (reference_type === 'metric' && (!metric || !metric_period))
          return {
            error: 'Metric and metric_period required for metric-based straight commission'
          }
      }

      if (type === 'field') {
        if (!percentage_column || !calculate_over_column)
          return {
            error:
              'Field-based commissions require both percentage_column and calculate_over_column'
          }
      }

      if (type === 'tiered') {
        if (!tiers?.length) return { error: 'Tiered commissions require tiers' }

        if (Boolean(calculate_over_column) === Boolean(calculate_over_metric))
          return {
            error:
              'Tiered commissions require exactly one of calculate_over_column or calculate_over_metric'
          }

        if (reference_type === 'column' && !column)
          return {
            error: 'Tiered column commissions require a column'
          }

        if (reference_type === 'target' && !targets?.length)
          return {
            error: 'Tiered target commissions require targets'
          }

        if (reference_type === 'metric' && (!metric || !metric_period))
          return {
            error: 'Tiered metric commissions require metric and metric_period'
          }

        if (reference_type === 'target') {
          const { team_target, team_ids } = formData
          if (team_target && (!team_ids || team_ids.length === 0))
            return {
              error: 'team_ids must be provided when team_target is true'
            }
        }

        if ((reference_type === 'target' || reference_type === 'metric') && !contributor)
          return {
            error: 'Contributor required for target or metric-based tiered commissions'
          }

        if (contributor && !isValidContributor(contributor))
          return { error: 'Invalid contributor value' }
      }

      break
    }

    case 'payment': {
      const { phases, recipients } = formData

      if (!phases?.length) return { error: 'At least one payment phase is required' }
      if (!recipients?.length) return { error: 'At least one recipient is required' }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const totalPhasePct = phases.reduce((s: number, p: any) => s + (p.percentage ?? 0), 0)
      if (totalPhasePct > 1) return { error: 'Total percentage across phases cannot exceed 100%' }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const totalRecipientPct = recipients.reduce((s: number, r: any) => s + (r.percentage ?? 0), 0)
      if (totalRecipientPct > 1)
        return {
          error: 'Total percentage across recipients cannot exceed 100%'
        }

      for (const phase of phases) {
        if (phase.timing === 'custom' && !phase.date_field)
          return { error: 'Custom-timed payment phases require date_field' }
        if (phase.timing === 'conditional' && !phase.filters)
          return { error: 'Conditional-timed payment phases require filters' }
      }

      break
    }

    default:
      break
  }

  return { error: null }
}

export interface StatementCurrencySummary {
  orgCurrency?: string
  teamCurrency?: string
  teamTotal?: number
}

export interface StatementBusinessUnitSummary {
  businessUnit?: string
  costCenter?: string
}

export interface StatementExportDynamicColumnLabels {
  typePrefix?: string
  categoryPrefix?: string
  typeLabels?: Record<string, string>
  categoryLabels?: Record<string, string>
}

type StatementExportPayout = Pick<Tables<'payouts'>, 'currency' | 'original_value' | 'value'>

function formatNumberForExport(value: number): string {
  const rounded = roundToDecimals(value, 2)
  return String(rounded)
}

const statementPayoutTotalPattern = /^payouts(?:_\d+)?(?:_[a-z]+)?_sum$/
const statementPayoutInstallmentTotalPattern = /^payout_installments(?:_\d+)?(?:_[a-z]+)?_sum$/

function shouldRoundNumericStringExportField(key: string) {
  return (
    statementPayoutTotalPattern.test(key) ||
    statementPayoutInstallmentTotalPattern.test(key) ||
    statementTypeTotalPattern.test(key) ||
    statementCategoryTotalPattern.test(key)
  )
}

function formatStatementExportValue(key: string, value: unknown) {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? roundToDecimals(value, 2) : value
  }

  if (typeof value === 'string' && shouldRoundNumericStringExportField(key)) {
    const trimmed = value.trim()
    if (trimmed === '') return value

    const num = Number(trimmed)
    if (Number.isFinite(num)) {
      return String(roundToDecimals(num, 2))
    }
  }

  return value
}

const EXPORT_NA_LABEL = 'N/A'
export const STATEMENT_EXPORT_TYPE_TOTAL_PREFIX_KEY = 'statement_export_type_total_prefix'
export const STATEMENT_EXPORT_CATEGORY_TOTAL_PREFIX_KEY = 'statement_export_category_total_prefix'
const DEFAULT_STATEMENT_EXPORT_TYPE_TOTAL_PREFIX = 'Compensation Type Total'
const DEFAULT_STATEMENT_EXPORT_CATEGORY_TOTAL_PREFIX = 'Compensation Category Total'

export const STATEMENT_EXPORT_COLUMN_OVERRIDES: Record<string, string> = {
  period_start: 'Statement Period Start',
  period_end: 'Statement Period End',
  status: 'Statement Status',
  has_pending_payouts: 'Has Pending Compensations',
  profiles_onboarded: 'Profile Onboarded',
  pending_approval: 'Pending Approval',
  payouts_sum: 'Total in Org currency',
  payouts_0_sum: 'Total in Org currency',
  payout_installments_amount_sum: 'Compensation Installments Total',
  payout_installments_0_sum: 'Compensation Installments Total',
  statement_approvals: 'Statement Approvals',
  issues: 'Statement Issues',
  cut_period: 'Cut Period',
  profiles_name: 'Profile Name',
  profiles_email: 'Profile Email',
  profiles_tax_id_number: 'Tax ID Number',
  profiles_teams_profiles_0_role: 'Primary Team Role',
  profiles_teams_profiles_0_teams_name: 'Primary Team Name',
  profiles_organizations_profiles_0_archived: 'Profile Archived',
  profiles_organizations_profiles_0_currency: 'Profile Currency',
  statement_business_unit: 'Business Unit',
  statement_cost_center: 'Cost Center'
}

export function getStatementCurrencySummary(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  row: Record<string, any>,
  payoutAlias = 'export_payouts'
): StatementCurrencySummary {
  const payouts: StatementExportPayout[] = Array.isArray(row[payoutAlias])
    ? (row[payoutAlias] as StatementExportPayout[])
    : []

  const teamProfiles = Array.isArray(row.profiles?.teams_profiles)
    ? row.profiles.teams_profiles
    : []
  const primaryTeam = teamProfiles[0]
  const teamCurrency =
    typeof primaryTeam?.teams?.currency === 'string' ? primaryTeam.teams.currency : undefined
  const orgCurrency =
    typeof row.organizations?.currency === 'string' ? row.organizations.currency : undefined
  const statementId = row.id ?? row.statement_id

  if (payouts.length === 0) {
    return {
      orgCurrency,
      teamCurrency: teamCurrency ?? orgCurrency,
      teamTotal: 0
    }
  }

  let teamTotal = 0
  const payoutCurrencies = new Set<string>()

  payouts.forEach((payout: StatementExportPayout) => {
    const currency = typeof payout?.currency === 'string' ? payout.currency : undefined

    if (payout.original_value === undefined && payout.value === undefined) {
      throw new Error(
        `Statement export failed${statementId ? ` for statement ${statementId}` : ''}: ` +
          'team total requires payout value on every payout.'
      )
    }

    if (currency) {
      payoutCurrencies.add(currency)
    }

    if (!!teamCurrency && teamCurrency !== orgCurrency) {
      teamTotal += payout.original_value ?? 0
    } else {
      teamTotal += payout.value ?? 0
    }
  })

  if (!teamCurrency && !orgCurrency) {
    throw new Error(
      `Statement export failed${statementId ? ` for statement ${statementId}` : ''}: ` +
        'team currency and organization currency are missing.'
    )
  }
  const effectiveTeamCurrency = teamCurrency ?? orgCurrency

  if (payoutCurrencies.size > 1 && !!teamCurrency && teamCurrency !== orgCurrency) {
    throw new Error(
      `Statement export failed${statementId ? ` for statement ${statementId}` : ''}: ` +
        `payouts original currency mismatch for team total. Expected ${effectiveTeamCurrency}, found ${Array.from(
          payoutCurrencies
        ).join(', ')}.`
    )
  }

  const [payoutCurrency] = Array.from(payoutCurrencies)
  if (
    !!payoutCurrency &&
    !!teamCurrency &&
    teamCurrency !== orgCurrency &&
    payoutCurrency !== teamCurrency
  ) {
    throw new Error(
      `Statement export failed${statementId ? ` for statement ${statementId}` : ''}: ` +
        `payouts original currency mismatch for team total. Expected ${teamCurrency}, found ${payoutCurrency}.`
    )
  }

  const roundedTeamTotal = roundToDecimals(teamTotal, 2)

  return {
    orgCurrency,
    teamCurrency: effectiveTeamCurrency,
    teamTotal: roundedTeamTotal
  }
}

export function addStatementCurrencyColumns(
  row: Record<string, unknown>,
  summary: StatementCurrencySummary,
  options?: { naLabel?: string }
) {
  const naLabel = options?.naLabel ?? EXPORT_NA_LABEL
  const formattedTeamTotal =
    typeof summary.teamTotal === 'number' ? formatNumberForExport(summary.teamTotal) : naLabel

  return {
    ...row,
    'Org Currency': summary.orgCurrency ?? naLabel,
    'Total to be paid': formattedTeamTotal,
    'To be paid currency': summary.teamCurrency ?? naLabel
  }
}

export function getStatementBusinessUnitSummary(
  row: Record<string, unknown>,
  profileData: Tables<'organizations_profiles_data'>[]
): StatementBusinessUnitSummary {
  const periodStart =
    typeof row.period_start === 'string' ? row.period_start.slice(0, 10) : undefined
  const periodStartTime = periodStart ? Date.parse(periodStart) : NaN

  if (!periodStart || Number.isNaN(periodStartTime) || profileData.length === 0) {
    return {}
  }

  const activeSnapshot = profileData.reduce<Tables<'organizations_profiles_data'> | undefined>(
    (latestSnapshot, snapshot) => {
      const snapshotTime = Date.parse(snapshot.from_date)

      if (Number.isNaN(snapshotTime) || snapshotTime > periodStartTime) {
        return latestSnapshot
      }

      if (!latestSnapshot) {
        return snapshot
      }

      return snapshotTime > Date.parse(latestSnapshot.from_date) ? snapshot : latestSnapshot
    },
    undefined
  )

  if (!activeSnapshot) {
    return {}
  }

  return {
    businessUnit:
      typeof activeSnapshot.business_unit_name === 'string'
        ? activeSnapshot.business_unit_name
        : undefined,
    costCenter:
      typeof activeSnapshot.business_unit_cost_center === 'string'
        ? activeSnapshot.business_unit_cost_center
        : undefined
  }
}

export function addStatementBusinessUnitColumns(
  row: Record<string, unknown>,
  summary: StatementBusinessUnitSummary,
  options?: { naLabel?: string }
) {
  const naLabel = options?.naLabel ?? EXPORT_NA_LABEL

  return {
    ...row,
    statement_business_unit: summary.businessUnit ?? naLabel,
    statement_cost_center: summary.costCenter ?? naLabel
  }
}

const indexedColumnPattern = /_\d+(_|$)/
const metaColumnPattern = /_meta$/
const statementTypeTotalPattern = /^sum_by_type_(.+)_sum$/
const statementCategoryTotalPattern = /^sum_by_category_(.+)_sum$/
const bannedPrefixes = ['export_payouts', 'payout_conversion', 'organizations']
const allowedIndexedStatementColumns = new Set([
  'profiles_teams_profiles_0_role',
  'profiles_teams_profiles_0_teams_name',
  'profiles_organizations_profiles_0_archived',
  'profiles_organizations_profiles_0_currency',
  'payouts_0_sum',
  'payout_installments_0_sum'
])

function shouldKeepExportColumn(key: string) {
  if (bannedPrefixes.some(prefix => key.startsWith(prefix))) {
    return false
  }

  if (
    key === 'id' ||
    key === 'flagged' ||
    key === 'org_id' ||
    key === 'organization_id' ||
    key === 'statement_id' ||
    key === 'profile_id' ||
    key === 'profiles_name_lower_case' ||
    key === 'profiles_name_normalized' ||
    key === 'profiles_img_url'
  ) {
    return false
  }

  if (key === 'meta') {
    return false
  }

  // Dynamic statement columns (sum_by_type_*, sum_by_category_*) have user-defined
  // labels that may contain digits; exclude them from the indexed column filter
  const isDynamicStatementColumn =
    statementTypeTotalPattern.test(key) || statementCategoryTotalPattern.test(key)
  if (
    !isDynamicStatementColumn &&
    indexedColumnPattern.test(key) &&
    !allowedIndexedStatementColumns.has(key)
  ) {
    return false
  }

  if (metaColumnPattern.test(key)) {
    return false
  }

  return true
}

export function filterExportColumns(row: Record<string, unknown>) {
  return Object.entries(row).reduce(
    (acc, [key, value]) => {
      if (shouldKeepExportColumn(key)) {
        acc[key] = value
      }
      return acc
    },
    {} as Record<string, unknown>
  )
}

function getDynamicStatementExportColumnName(
  key: string,
  overrides: Record<string, string>,
  dynamicLabels?: StatementExportDynamicColumnLabels
) {
  const typeMatch = key.match(statementTypeTotalPattern)
  if (typeMatch) {
    const rawType = typeMatch[1]
    const typeLabel = dynamicLabels?.typeLabels?.[rawType] ?? rawType
    const typePrefix =
      dynamicLabels?.typePrefix ??
      overrides[STATEMENT_EXPORT_TYPE_TOTAL_PREFIX_KEY] ??
      DEFAULT_STATEMENT_EXPORT_TYPE_TOTAL_PREFIX
    return `${typePrefix}: ${typeLabel}`
  }

  const categoryMatch = key.match(statementCategoryTotalPattern)
  if (categoryMatch) {
    const rawCategory = categoryMatch[1]
    const categoryLabel = dynamicLabels?.categoryLabels?.[rawCategory] ?? rawCategory
    const categoryPrefix =
      dynamicLabels?.categoryPrefix ??
      overrides[STATEMENT_EXPORT_CATEGORY_TOTAL_PREFIX_KEY] ??
      DEFAULT_STATEMENT_EXPORT_CATEGORY_TOTAL_PREFIX
    return `${categoryPrefix}: ${categoryLabel}`
  }

  return undefined
}

export function renameStatementExportColumns(
  row: Record<string, unknown>,
  overrides: Record<string, string>,
  dynamicLabels?: StatementExportDynamicColumnLabels
) {
  return Object.entries(row).reduce(
    (acc, [key, value]) => {
      const columnName =
        overrides[key] ?? getDynamicStatementExportColumnName(key, overrides, dynamicLabels) ?? key
      acc[columnName] = formatStatementExportValue(key, value)
      return acc
    },
    {} as Record<string, unknown>
  )
}
